/**
 * First-run onboarding wizard.
 *
 * Shown automatically the first time a new school logs in (no school name set,
 * no academic years configured). Walks the user through:
 *   1. School name + logo
 *   2. First academic year (e.g. "26/27")
 *   3. Adding their first students (single or bulk)
 *
 * Closing the wizard saves the partial state — they can resume from
 * Settings → School Details if they bail.
 */

import { db } from '../firebase.js';
import { doc, setDoc, addDoc, collection } from 'firebase/firestore';
import { toast } from './toast.js';
import { friendlyError } from '../utils/friendly-error.js';

import { setHtml } from '../utils/dom-set.js';

import { esc } from '../utils/dom.js';
function thisYearLabel() {
  const y = new Date().getFullYear();
  return String(y - 1).slice(-2) + '/' + String(y).slice(-2);
}

let overlay = null;
let step = 1;
const wizardData = {
  schoolName: '',
  logo: '',
  year: thisYearLabel(),
  students: [], // [{ name, yearLevel, parentName, parentEmail }]
};

function render() {
  if (!overlay) return;
  const body = overlay.querySelector('.wizard-body');
  if (step === 1) {
    setHtml(body, `
      <h2 class="onboarding-step-h2">Welcome to Easy Clarity 4 Growth</h2>
      <p class="onboarding-brand-tagline">Evidence And Support for You</p>
      <p class="text-muted onboarding-step-intro">Three quick steps to get your school set up. You can change any of this later in Settings.</p>
      <div class="wizard-step-indicator">
        <span class="active">1 · School</span><span>2 · Year</span><span>3 · Students</span>
      </div>
      <div class="form-group onboarding-form-block">
        <label>School Name</label>
        <input type="text" id="wzSchoolName" value="${esc(wizardData.schoolName)}" placeholder="Your school's full name">
      </div>
      <div class="form-group">
        <label>School Logo (optional)</label>
        <div class="onboarding-logo-row">
          ${wizardData.logo ? `<img src="${esc(wizardData.logo)}" class="onboarding-logo-img" alt="">` : '<span class="text-muted onboarding-no-logo">No logo uploaded</span>'}
          <button class="btn btn-outline btn-sm" id="wzLogoBtn">Upload Logo</button>
          <input type="file" accept="image/*" id="wzLogoFile" class="file-input-hidden">
        </div>
      </div>
      <div class="wizard-actions">
        <button class="btn btn-outline" id="wzSkip">Skip for now</button>
        <button class="btn btn-primary" id="wzNext1">Continue →</button>
      </div>`);
    // Mirror the school-name input into wizardData on every keystroke, so a
    // re-render (e.g. after the logo uploads) preserves whatever the user typed.
    body.querySelector('#wzSchoolName')?.addEventListener('input', (e) => {
      wizardData.schoolName = e.target.value;
    });
    body.querySelector('#wzLogoBtn')?.addEventListener('click', () => body.querySelector('#wzLogoFile').click());
    body.querySelector('#wzLogoFile')?.addEventListener('change', async (e) => {
      const f = e.target.files?.[0]; if (!f) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const scale = Math.min(400 / img.width, 150 / img.height, 1);
          canvas.width = img.width * scale; canvas.height = img.height * scale;
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          wizardData.logo = canvas.toDataURL('image/png', 0.9);
          render();
        };
        img.src = ev.target.result;
      };
      reader.readAsDataURL(f);
    });
    body.querySelector('#wzNext1')?.addEventListener('click', () => {
      wizardData.schoolName = body.querySelector('#wzSchoolName').value.trim();
      if (!wizardData.schoolName) { toast('Enter your school name'); return; }
      step = 2; render();
    });
    body.querySelector('#wzSkip')?.addEventListener('click', close);
  } else if (step === 2) {
    setHtml(body, `
      <h2 class="onboarding-step-h2">Set up your academic year</h2>
      <p class="text-muted onboarding-step-intro">Australian school years run across two calendar years (e.g. 26/27 = Term 3 2026 → Term 2 2027).</p>
      <div class="wizard-step-indicator">
        <span>1 · School</span><span class="active">2 · Year</span><span>3 · Students</span>
      </div>
      <div class="form-group onboarding-form-block">
        <label>Current Academic Year</label>
        <input type="text" id="wzYear" value="${esc(wizardData.year)}" placeholder="e.g. 26/27" inputmode="numeric" maxlength="5" autocomplete="off">
        <p class="text-muted onboarding-year-hint" id="wzYearHint">Format: two digits / two digits, e.g. 25/26 - second year must follow the first.</p>
      </div>
      <div class="wizard-actions">
        <button class="btn btn-outline" id="wzBack2">← Back</button>
        <button class="btn btn-primary" id="wzNext2">Continue →</button>
      </div>`);
    // Filter input: digits only, auto-insert the slash after 2 digits, mirror
    // into wizardData on every keystroke so re-renders keep the value.
    const yearInput = body.querySelector('#wzYear');
    const yearHint = body.querySelector('#wzYearHint');
    function formatYearInput(raw) {
      const digits = String(raw || '').replace(/\D/g, '').slice(0, 4);
      if (digits.length <= 2) return digits;
      return digits.slice(0, 2) + '/' + digits.slice(2);
    }
    function validYear(v) {
      const m = /^(\d{2})\/(\d{2})$/.exec(v);
      if (!m) return false;
      const a = parseInt(m[1], 10);
      const b = parseInt(m[2], 10);
      return b === (a + 1) % 100;
    }
    yearInput?.addEventListener('input', (e) => {
      const formatted = formatYearInput(e.target.value);
      if (formatted !== e.target.value) e.target.value = formatted;
      wizardData.year = formatted;
      // Inline hint colouring: red if non-empty and invalid, muted otherwise.
      if (yearHint) {
        const ok = !formatted || validYear(formatted);
        yearHint.classList.toggle('text-error', !ok);
        yearHint.classList.toggle('text-muted', ok);
      }
    });
    body.querySelector('#wzBack2')?.addEventListener('click', () => { step = 1; render(); });
    body.querySelector('#wzNext2')?.addEventListener('click', () => {
      const v = (yearInput?.value || '').trim();
      if (!validYear(v)) {
        toast('Enter the year as two digits / two consecutive digits, e.g. 25/26');
        if (yearHint) { yearHint.classList.remove('text-muted'); yearHint.classList.add('text-error'); }
        yearInput?.focus();
        return;
      }
      wizardData.year = v;
      step = 3; render();
    });
  } else if (step === 3) {
    setHtml(body, `
      <h2 class="onboarding-step-h2">Add your first students</h2>
      <p class="text-muted u-mb-6">You can add a few now to get started, or skip and bulk-add them in the Admin section later.</p>
      <div class="wizard-step-indicator">
        <span>1 · School</span><span>2 · Year</span><span class="active">3 · Students</span>
      </div>
      <div id="wzStudentsList" class="onboarding-students-list"></div>
      <div class="onboarding-step3-actions">
        <button class="btn btn-outline btn-sm onboarding-add-stu" id="wzAddStu">+ Add another student</button>
        <span class="text-muted onboarding-step3-or">or</span>
        <button class="btn btn-outline btn-sm onboarding-bulk-import" id="wzBulkImport">Open the Bulk Editor grid</button>
      </div>
      <div class="wizard-actions">
        <button class="btn btn-outline" id="wzBack3">← Back</button>
        <button class="btn btn-primary" id="wzFinish">Finish Setup ✓</button>
      </div>`);
    function paintList() {
      const list = body.querySelector('#wzStudentsList');
      if (!wizardData.students.length) {
        setHtml(list, '<p class="text-muted onboarding-students-empty">No students added yet - click "+ Add another student" or skip and add them later in Admin.</p>');
        return;
      }
      setHtml(list, wizardData.students.map((s, i) => `
        <div class="onboarding-stu-row">
          <input type="text" class="wz-stu-name" data-i="${i}" value="${esc(s.name)}" placeholder="Student name">
          <select class="wz-stu-yl" data-i="${i}">
            ${['Prep','Year 1','Year 2','Year 3','Year 4','Year 5','Year 6'].map(yl => `<option value="${yl}" ${s.yearLevel === yl ? 'selected' : ''}>${yl}</option>`).join('')}
          </select>
          <button class="btn btn-outline btn-sm wz-stu-del onboarding-stu-del" data-i="${i}">×</button>
        </div>`).join(''));
      list.querySelectorAll('.wz-stu-name').forEach(el => el.addEventListener('input', () => { wizardData.students[+el.dataset.i].name = el.value; }));
      list.querySelectorAll('.wz-stu-yl').forEach(el => el.addEventListener('change', () => { wizardData.students[+el.dataset.i].yearLevel = el.value; }));
      list.querySelectorAll('.wz-stu-del').forEach(btn => btn.addEventListener('click', () => { wizardData.students.splice(+btn.dataset.i, 1); paintList(); }));
    }
    paintList();
    body.querySelector('#wzAddStu')?.addEventListener('click', () => {
      wizardData.students.push({ name: '', yearLevel: 'Prep' });
      paintList();
      const inputs = body.querySelectorAll('.wz-stu-name');
      inputs[inputs.length - 1]?.focus();
    });
    body.querySelector('#wzBulkImport')?.addEventListener('click', async () => {
      // Persist the school + year first so the rest of the app behaves as
      // if onboarding is complete, then hand off to the bulk import wizard.
      try {
        await setDoc(doc(db, 'settings', 'school'), {
          name: wizardData.schoolName,
          logo: wizardData.logo,
          years: [wizardData.year],
        }, { merge: true });
      } catch (err) {
        toast(friendlyError(err, "Couldn't save the setup. Please try again."));
        return;
      }
      close();
      // In-app Student List grid — no CSV round-trip.
      const { showStudentsEditor } = await import('./students-editor.js');
      showStudentsEditor();
    });
    body.querySelector('#wzBack3')?.addEventListener('click', () => { step = 2; render(); });
    body.querySelector('#wzFinish')?.addEventListener('click', finish);
  }
}

async function finish() {
  try {
    // 1. Save school settings
    await setDoc(doc(db, 'settings', 'school'), {
      name: wizardData.schoolName,
      logo: wizardData.logo,
      years: [wizardData.year],
    }, { merge: true });
    // 2. Create students + their enrolments
    for (const s of wizardData.students) {
      const cleanName = (s.name || '').trim();
      if (!cleanName) continue;
      const studentRef = await addDoc(collection(db, 'students'), {
        name: cleanName,
        nccdStatus: '',
        category: '',
        level: '',
        diagnoses: [],
        createdAt: new Date().toISOString(),
      });
      await addDoc(collection(db, 'enrolments'), {
        studentId: studentRef.id,
        academicYear: wizardData.year,
        yearLevel: s.yearLevel,
      });
    }
    toast('All set! Welcome to Easy Clarity 4 Growth.');
    close();
    // Trigger a soft reload so window.nccd state picks up the new data
    setTimeout(() => location.reload(), 400);
  } catch (err) {
    toast(friendlyError(err, "Couldn't save the setup. Please try again."));
  }
}

function close() {
  if (overlay) { overlay.remove(); overlay = null; }
}

export function showOnboardingWizard() {
  if (overlay) return;
  overlay = document.createElement('div');
  overlay.className = 'wizard-overlay';
  setHtml(overlay, `<div class="wizard-modal"><div class="wizard-body"></div></div>`);
  document.body.appendChild(overlay);
  step = 1;
  render();
}

/**
 * Check the current Firestore state and decide whether to show the wizard.
 * Called once after sign-in completes from main.js.
 */
export function maybeShowOnboarding() {
  const settings = window.nccd?.settings || {};
  const students = window.nccd?.students || [];
  const noSchool = !settings.name;
  const noYears = !(settings.years && settings.years.length);
  const noStudents = students.length === 0;
  if (noSchool && noYears && noStudents) {
    showOnboardingWizard();
  }
}
